import { DEFAULT_WEBSITE_LEADERSHIP } from './website-leadership-defaults';
export { DEFAULT_WEBSITE_LEADERSHIP };
export type LeadershipGroupId = 'office-bearers' | 'executive-committee' | 'advisors';
export type LeadershipMember = {
    id: string;
    name: string;
    position: string;
    photo: string | null;
    photoWebp: string | null;
    photoPosition: string;
};
export type LeadershipGroup = {
    id: LeadershipGroupId;
    members: LeadershipMember[];
};
export type WebsiteLeadership = {
    title: string;
    subtitle: string;
    groups: LeadershipGroup[];
    updatedAt: string | null;
};
export const LEADERSHIP_GROUPS: {
    id: LeadershipGroupId;
    label: string;
}[] = [
    { id: 'office-bearers', label: 'Office Bearers' }, { id: 'executive-committee', label: 'Executive Committee' }, { id: 'advisors', label: 'Advisors' },
];
export const WEBSITE_LEADERSHIP_KEY = 'websiteLeadership';
export const LEADERSHIP_IMAGE_PATH_PREFIX = '/api/public/site-content/leadership/images/';
export const LEADERSHIP_PHOTO_MAX_BYTES = 1572864;
export const LEADERSHIP_IMAGE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export function isLeadershipImageUrl(value: unknown): value is string {
    return typeof value === 'string' && value.startsWith(LEADERSHIP_IMAGE_PATH_PREFIX) && LEADERSHIP_IMAGE_ID_PATTERN.test(value.slice(LEADERSHIP_IMAGE_PATH_PREFIX.length));
}
const legacy = new Set(DEFAULT_WEBSITE_LEADERSHIP.groups.flatMap(g => g.members.map(m => m.photo)));
const webpPairs = new Map(DEFAULT_WEBSITE_LEADERSHIP.groups.flatMap(g => g.members.filter(m => m.photoWebp).map(m => [m.photo, m.photoWebp] as const)));
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const keys = (v: Record<string, unknown>, allowed: string[]) => Object.keys(v).length === allowed.length && Object.keys(v).every(k => allowed.includes(k));
const text = (v: unknown, max: number, required = false): v is string => typeof v === 'string' && v.trim().length <= max && (!required || !!v.trim());
export function normalizeWebsiteLeadershipInput(input: unknown): {
    ok: true;
    leadership: WebsiteLeadership;
} | {
    ok: false;
    error: string;
} {
    const fail = { ok: false as const, error: 'Invalid leadership data. Check the title, groups, people and photo paths.' };
    if (!object(input) || !keys(input, ['title', 'subtitle', 'groups', 'updatedAt']) || !text(input.title, 120, true) || !text(input.subtitle, 500) || !(input.updatedAt === null || (typeof input.updatedAt === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(input.updatedAt) && !Number.isNaN(Date.parse(input.updatedAt)))) || !Array.isArray(input.groups) || input.groups.length !== 3)
        return fail;
    const ids = new Set<string>();
    let count = 0;
    const groups: LeadershipGroup[] = [];
    for (const [i, group] of input.groups.entries()) {
        if (!object(group) || !keys(group, ['id', 'members']) || group.id !== LEADERSHIP_GROUPS[i].id || !Array.isArray(group.members) || group.members.length > 50)
            return fail;
        const members: LeadershipMember[] = [];
        for (const member of group.members) {
            if (!object(member) || !keys(member, ['id', 'name', 'position', 'photo', 'photoWebp', 'photoPosition']) || typeof member.id !== 'string' || !/^[a-z0-9][a-z0-9-]{0,79}$/.test(member.id) || ids.has(member.id) || !text(member.name, 120, true) || !text(member.position, 120) || !(member.photo === null || (typeof member.photo === 'string' && (legacy.has(member.photo) || isLeadershipImageUrl(member.photo)))) || !(member.photoWebp === null || (typeof member.photoWebp === 'string' && typeof member.photo === 'string' && webpPairs.get(member.photo) === member.photoWebp)) || typeof member.photoPosition !== 'string' || !/^(top|center|center (?:100|[1-9]?\d)%)$/.test(member.photoPosition))
                return fail;
            ids.add(member.id);
            if (++count > 100)
                return fail;
            members.push({ id: member.id, name: member.name.trim(), position: member.position.trim(), photo: member.photo as string | null, photoWebp: member.photoWebp as string | null, photoPosition: member.photoPosition });
        }
        groups.push({ id: LEADERSHIP_GROUPS[i].id, members });
    }
    return { ok: true, leadership: { title: input.title.trim(), subtitle: input.subtitle.trim(), groups, updatedAt: input.updatedAt as string | null } };
}
export function parseWebsiteLeadership(valueJson: string | null | undefined): WebsiteLeadership | null {
    try {
        const result = normalizeWebsiteLeadershipInput(JSON.parse(valueJson ?? ''));
        return result.ok ? result.leadership : null;
    }
    catch {
        return null;
    }
}
